import type { OnboardingLane, OnboardingSignupHint } from './signupActivitySession';

export const ONBOARDING_CORE_TOTAL_DEFAULT = 6;
/** Pension-only : module + boxes remplacent le 1er service catalogue. */
export const ONBOARDING_CORE_TOTAL_STAYS = 7;
/** Mixed : 1er service catalogue + module + boxes. */
export const ONBOARDING_CORE_TOTAL_MIXED = 8;

export const PENSION_SETUP_PATH = '/app/pension?tab=config&setup=1';
export const PENSION_ADD_STAY_PATH = '/app/pension?tab=planning&addStay=1';
export const PENSION_MODULE_PATH = '/app/settings?focus=boarding';

export type PensionSetupSnapshot = {
  isBoardingEnabled: boolean;
  poolCount: number;
  unitCount: number;
  stayCount: number;
};

export function emptyPensionSetupSnapshot(
  isBoardingEnabled = false
): PensionSetupSnapshot {
  return {
    isBoardingEnabled,
    poolCount: 0,
    unitCount: 0,
    stayCount: 0,
  };
}

export function isStaysOnboarding(hint: OnboardingSignupHint): boolean {
  return hint === 'stays';
}

export function onboardingCoreTotal(lane: OnboardingLane): number {
  if (lane === 'stays') return ONBOARDING_CORE_TOTAL_STAYS;
  if (lane === 'mixed') return ONBOARDING_CORE_TOTAL_MIXED;
  return ONBOARDING_CORE_TOTAL_DEFAULT;
}

export function isPensionModuleReady(
  snapshot: Pick<PensionSetupSnapshot, 'isBoardingEnabled'>
): boolean {
  return Boolean(snapshot.isBoardingEnabled);
}

export function isPensionInventoryReady(
  snapshot: Pick<PensionSetupSnapshot, 'poolCount' | 'unitCount'>
): boolean {
  return snapshot.poolCount > 0 && snapshot.unitCount > 0;
}

export function isPensionFirstStayDone(
  snapshot: Pick<PensionSetupSnapshot, 'stayCount'>
): boolean {
  return snapshot.stayCount > 0;
}

/** 0–2 ticks core à la place du 1er service catalogue. */
export function countPensionCoreServiceTicks(
  snapshot: PensionSetupSnapshot
): number {
  return (
    (isPensionModuleReady(snapshot) ? 1 : 0) +
    (isPensionInventoryReady(snapshot) ? 1 : 0)
  );
}

export function asListedLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

export function parsePensionSetupFromApi(payloads: {
  pools?: unknown;
  units?: unknown;
  bookings?: unknown;
}): Pick<PensionSetupSnapshot, 'poolCount' | 'unitCount' | 'stayCount'> {
  const poolsPayload = payloads.pools as
    | { pools?: unknown; data?: { pools?: unknown } }
    | unknown[]
    | undefined;
  let pools: unknown = [];
  if (Array.isArray(poolsPayload)) {
    pools = poolsPayload;
  } else if (poolsPayload && typeof poolsPayload === 'object') {
    pools =
      (poolsPayload as { pools?: unknown }).pools ??
      (poolsPayload as { data?: { pools?: unknown } }).data?.pools ??
      [];
  }

  const bookingsPayload = payloads.bookings as
    | { bookings?: unknown }
    | unknown[]
    | undefined;
  let bookings: unknown = [];
  if (Array.isArray(bookingsPayload)) {
    bookings = bookingsPayload;
  } else if (bookingsPayload && typeof bookingsPayload === 'object') {
    bookings = (bookingsPayload as { bookings?: unknown }).bookings ?? [];
  }

  return {
    poolCount: asListedLength(pools),
    unitCount: asListedLength(payloads.units),
    stayCount: asListedLength(bookings),
  };
}

export function hasPensionSetupQuery(search: string): boolean {
  return new URLSearchParams(search).get('setup') === '1';
}

export function hasPensionAddStayQuery(search: string): boolean {
  return new URLSearchParams(search).get('addStay') === '1';
}

export function stripPensionOnboardingParams(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('setup');
  params.delete('addStay');
  const next = params.toString();
  return next ? `?${next}` : '';
}

export const BOARDING_JUST_SETUP_STORAGE_KEY = 'boarding:just-setup';
export const BOARDING_PROMPT_FIRST_STAY_KEY = 'boarding:prompt-first-stay';

export function shouldDeferPensionAddStay(
  search: string,
  unitCount: number
): boolean {
  return hasPensionAddStayQuery(search) && unitCount === 0;
}

export function shouldOpenPensionAddStayModal(
  search: string,
  unitCount: number
): boolean {
  return hasPensionAddStayQuery(search) && unitCount > 0;
}

function writeSessionFlag(key: string, value: '1' | null): void {
  try {
    if (value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
  } catch {
    /* ignore */
  }
}

export function markPensionFirstStayPrompt(): void {
  writeSessionFlag(BOARDING_PROMPT_FIRST_STAY_KEY, '1');
}

export function markPensionJustSetup(): void {
  writeSessionFlag(BOARDING_JUST_SETUP_STORAGE_KEY, '1');
  markPensionFirstStayPrompt();
}

export function clearPensionFirstStayPrompt(): void {
  writeSessionFlag(BOARDING_PROMPT_FIRST_STAY_KEY, null);
  writeSessionFlag(BOARDING_JUST_SETUP_STORAGE_KEY, null);
}

export function hasPensionFirstStayPrompt(): boolean {
  try {
    return (
      sessionStorage.getItem(BOARDING_PROMPT_FIRST_STAY_KEY) === '1' ||
      sessionStorage.getItem(BOARDING_JUST_SETUP_STORAGE_KEY) === '1'
    );
  } catch {
    return false;
  }
}
